import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

// Vite replaces import.meta.env.VITE_* at build time
// These must be accessed directly (not via object property) for static replacement
const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
const configuredStorageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID;
const appId = import.meta.env.VITE_FIREBASE_APP_ID;

// Validate that all required env vars are present
const missingVars = [
  { name: 'VITE_FIREBASE_API_KEY', value: apiKey },
  { name: 'VITE_FIREBASE_AUTH_DOMAIN', value: authDomain },
  { name: 'VITE_FIREBASE_PROJECT_ID', value: projectId },
  { name: 'VITE_FIREBASE_MESSAGING_SENDER_ID', value: messagingSenderId },
  { name: 'VITE_FIREBASE_APP_ID', value: appId },
].filter(({ value }) => !value);

if (missingVars.length > 0) {
  throw new Error(
    `Missing Firebase environment variables: ${missingVars.map((v) => v.name).join(', ')}`,
  );
}

// Prefer the current Firebase Storage bucket domain and avoid stale appspot values.
const storageBucket =
  configuredStorageBucket?.endsWith('.appspot.com') || !configuredStorageBucket
    ? `${projectId}.firebasestorage.app`
    : configuredStorageBucket;

const firebaseConfig = {
  apiKey,
  authDomain,
  projectId,
  storageBucket,
  messagingSenderId,
  appId,
};

export const firebaseApp = initializeApp(firebaseConfig);

// ─── App Check (reCAPTCHA v3) ─────────────────────────────────────────────
// Opt-in via VITE_APPCHECK_RECAPTCHA_SITE_KEY. Without the key, App Check
// is not initialized and the backend should keep enforcement disabled.
// Use VITE_APPCHECK_DEBUG_TOKEN=<token-or-true> when running locally.
const recaptchaSiteKey = import.meta.env.VITE_APPCHECK_RECAPTCHA_SITE_KEY;
if (recaptchaSiteKey) {
  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  if (debugToken) {
    // Firebase reads this global to issue a debug token in dev.
    (
      self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN: string | boolean }
    ).FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken === 'true' ? true : debugToken;
  }
  try {
    initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaV3Provider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    console.info('[Firebase] App Check initialized');
  } catch (e) {
    console.warn('[Firebase] App Check init failed', e);
  }
}

export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const functions = getFunctions(firebaseApp, 'us-central1');
export const storage = getStorage(firebaseApp);

// ─── Connect to emulators in development ─────────────────────────────────
// Los puertos se pueden cambiar (VITE_EMULATOR_*_PORT) para correr un segundo
// emulador cuando otro proyecto ya ocupa los de siempre; por defecto, los de
// firebase.json.
const emuPort = (name: string, fallback: number): number =>
  Number((import.meta.env as Record<string, string | undefined>)[`VITE_EMULATOR_${name}_PORT`]) ||
  fallback;
if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, `http://localhost:${emuPort('AUTH', 9099)}`, { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', emuPort('FIRESTORE', 8080));
  connectFunctionsEmulator(functions, '127.0.0.1', emuPort('FUNCTIONS', 5001));
  connectStorageEmulator(storage, '127.0.0.1', emuPort('STORAGE', 9199));
  console.info('[Firebase] Connected to emulators');
} else if (import.meta.env.VITE_FUNCTIONS_EMULATOR === 'true') {
  // ─── SÓLO LAS FUNCIONES EN LOCAL, EL RESTO REAL ────────────────────────
  //
  // PARA PROBAR UN CALLABLE NUEVO SIN DESPLEGARLO. Se edita el caso de uso,
  // el emulador recarga, y la pantalla ya llama al código nuevo. Un
  // despliegue directo, además de saltarse el pipeline, deja en el servidor
  // código que no está en `main`: el siguiente merge lo revierte sin avisar.
  //
  // AUTENTICACIÓN Y DATOS SIGUEN SIENDO LOS REALES, a propósito: con el
  // emulador completo hay que crear un usuario, un inquilino, una sección y
  // veinte alumnos antes de poder mirar nada, y lo que se quiere mirar es
  // cómo se ve un curso DE VERDAD.
  //
  // ⚠️ POR ESO MISMO, LAS ESCRITURAS SON REALES. La función corre con
  // credenciales de administrador contra la base del proyecto: si desde esta
  // pantalla se cierra un acta o se borra algo, se cierra y se borra de
  // verdad. Es para leer y mirar, no para probar acciones destructivas.
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  console.warn(
    '[Firebase] Funciones en el emulador local; auth y datos son los REALES del proyecto',
  );
}
